import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { api } from "../../data";
import { BarChart } from "../../components/charts";
import { monthShort } from "../../lib/format";
import { countText, shareText, type AudienceBlock, type Breakdown, type InsightsLang, type InsightsView, type MonthlyView, type PostKind, type PostsView, type StoriesView } from "./insights-model";
import "./insights.css";

/**
 * Insights im Instagram-Look (Entscheidung des Inhabers vom 02.10.2026, 0164): ein Baustein für die Marke (nach bestandener
 * Prüfung, 13_oeffentlich/insights.html, Umfang OE-031), die Vorschau im Agentur-Bereich und die eigenen Insights im Creator-Portal.
 * Aufbau wie das Professional Dashboard der Instagram-App: Profilkopf mit Farbring, Übersicht als Liste, Follower/Nicht-Follower
 * als Ring, Content-Arten als Balken, Top-Inhalte als Raster mit Vorschaubildern, Zielgruppe mit Umschalter. Hell/Dunkel folgt dem
 * Portal-Schalter. Blöcke ohne Werte entstehen gar nicht erst; alle Texte vom Server landen nur als Text im Dokument; jede Grafik
 * hat ihre Zahlen zusätzlich als Text oder Tabelle (Vorlesen, Kopieren).
 * Sprache: die der Ansicht (buildView mit Sprache; SICHT-OEFFENTLICH-BETRIEB-04), Standard Deutsch.
 */
const tt = (lang: InsightsLang) => (de: string, enText: string) => (lang === "en" ? enText : de);
const dateText = (iso: string, lang: InsightsLang) => new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE");

/**
 * Abschnittsüberschriften (SICHT-OEFFENTLICH-BETRIEB-21): auf der Seite direkt unter dem h1 als h2, in der Vorschau im
 * Agentur-Bereich (Titel h3) als h4; vorher sprang die Gliederung von h1 auf h3. Aussehen über .tk-ins-h.
 */
type Stufe = "h2" | "h4";
function Ue({ as: Tag, children }: { as: Stufe; children: ReactNode }) {
  return <Tag className="tk-ins-h">{children}</Tag>;
}

/** Symbole wie in der Instagram-App (Linien, erben die Schriftfarbe) */
function Icon({ name }: { name: "reel" | "carousel" | "play" | "heart" | "comment" | "save" | "share" | "grid" }) {
  const p = { width: 18, height: 18, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (name) {
    case "reel": return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="5" /><path d="M3 8h18M8.5 3l3 5M14 3l3 5" /><path d="M10 11.5v5l4.5-2.5z" fill="currentColor" /></svg>;
    case "carousel": return <svg {...p}><rect x="7" y="7" width="14" height="14" rx="3" /><path d="M17 3H6a3 3 0 0 0-3 3v11" /></svg>;
    case "play": return <svg {...p} strokeWidth={0}><path d="M7 4.5v15l12.5-7.5z" fill="currentColor" /></svg>;
    case "heart": return <svg {...p}><path d="M12 20s-7-4.4-9.2-8.6C1.2 8.3 3 4.5 6.6 4.5c2.1 0 3.4 1.3 5.4 3.4 2-2.1 3.3-3.4 5.4-3.4 3.6 0 5.4 3.8 3.8 6.9C19 15.6 12 20 12 20z" /></svg>;
    case "comment": return <svg {...p}><path d="M20.5 11.5a8.5 8.5 0 0 1-12.6 7.4L3.5 20.5l1.6-4.3A8.5 8.5 0 1 1 20.5 11.5z" /></svg>;
    case "save": return <svg {...p}><path d="M19 21l-7-5-7 5V4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1z" /></svg>;
    case "share": return <svg {...p}><path d="M22 3L9.2 10.2M22 3l-7 18-3.8-8.8L2 8.5z" /></svg>;
    default: return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M15 3v18M3 9h18M3 15h18" /></svg>;
  }
}

/**
 * own (0156): die eigenen Insights des Creators im Creator-Portal mit denselben Bausteinen; ohne „Freigegeben für“ und ohne
 * Freigabeumfang, dafür mit der Herkunft der Zahlen. periodControl: Zeitraumwahl im Kopf (sonst der feste Zeitraum der Freigabe).
 */
export function InsightsAnsicht({ view, embedded = false, own = false, periodControl }: { view: InsightsView; embedded?: boolean; own?: boolean; periodControl?: ReactNode }) {
  const lang = view.lang ?? "de";
  const t = tt(lang);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!embedded) heading.current?.focus();
  }, [embedded]);
  const h: Stufe = embedded ? "h4" : "h2";
  const split = view.breakdowns.find((b) => b.key === "follower_split" && b.list && b.list.length > 0) ?? null;
  const formats = view.breakdowns.filter((b) => b !== split);
  // 0340: Reiter „Übersicht“ und „Zielgruppe“ statt einer langen Seite; nur wenn beide Seiten etwas zeigen
  const hasOverview = view.tiles.length > 0 || !!view.trend || !!split || formats.length > 0 || !!view.stories || !!view.posts || !!view.monthly;
  const hasAudience = view.audience.length > 0;
  const tabs = hasOverview && hasAudience;
  const [tab, setTab] = useState<"overview" | "audience">("overview");
  const showOverview = !tabs || tab === "overview";
  const showAudience = !tabs || tab === "audience";
  const tabId = useId();
  return (
    <div className="tk-igx">
      {/* Eingebettet (Vorschau im Agentur-Bereich): kein Seitentitel, kein Fokussprung, Überschrift eine Ebene tiefer */}
      {!embedded && <title>{`${view.title} · Insights · ${view.agency || "CreatorOffice"}`}</title>}

      <header className="igx-head">
        <div className={`igx-ava${view.picture ? " has-img" : ""}`} aria-hidden="true">
          <span className="igx-ava-in">{view.picture ? <img src={view.picture} alt="" referrerPolicy="no-referrer" /> : (view.initials ?? "IG")}</span>
        </div>
        <div className="igx-who">
          <div className="eyebrow igx-eyebrow">{own ? t("Deine Insights von Instagram", "Your insights from Instagram") : <>{view.agency ? `${view.agency} · ` : ""}{t("Freigegeben für", "Shared with")} {view.email}</>}</div>
          {embedded ? <h3 className="igx-title">{view.title}</h3> : <h1 ref={heading} tabIndex={-1} className="igx-title">{view.title}</h1>}
          {view.username && <div className="igx-handle">@{view.username}</div>}
          {view.profileTiles.length > 0 && (
            <ul className="igx-stats">
              {view.profileTiles.map((x) => <li key={x.key}><b className="num">{x.display}</b><span>{x.label}</span></li>)}
            </ul>
          )}
        </div>
        <div className="igx-period">{periodControl ?? <span className="igx-pill">{view.periodLabel}</span>}</div>
      </header>
      <p className="igx-sub">{view.subline}{view.asOf ? ` · ${t("Stand", "As of")} ${dateText(view.asOf, lang)}` : ""}{view.expiresAt ? ` · ${t("Freigabe gültig bis", "Shared until")} ${dateText(view.expiresAt, lang)}` : ""}</p>

      {tabs && (
        <div className="igx-tabs" role="tablist" aria-label={t("Bereich der Insights", "Insights section")}>
          {(["overview", "audience"] as const).map((k) => (
            <button key={k} type="button" role="tab" id={`${tabId}-${k}`} aria-controls={`${tabId}-${k}-panel`} aria-selected={tab === k} tabIndex={tab === k ? 0 : -1}
              onClick={() => setTab(k)}
              onKeyDown={(e) => {
                if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
                const next = tab === "overview" ? "audience" : "overview";
                setTab(next);
                document.getElementById(`${tabId}-${next}`)?.focus();
              }}>
              {k === "overview" ? t("Übersicht", "Overview") : t("Zielgruppe", "Audience")}
            </button>
          ))}
        </div>
      )}

      <div role={tabs ? "tabpanel" : undefined} id={`${tabId}-overview-panel`} aria-labelledby={tabs ? `${tabId}-overview` : undefined} hidden={!showOverview} className="igx-panel">
        {view.tiles.length > 0 && (
          <section className="igx-card">
            <Ue as={h}>{t("Übersicht", "Overview")}</Ue>
            <ul className="igx-list">
              {view.tiles.map((x) => (
                <li key={x.key}>
                  <div><span className="igx-l">{x.label}</span><small>{x.sub}</small></div>
                  <div className="igx-vbox">
                    <b className="num igx-v">{x.display}</b>
                    {x.compare && <small className={`igx-cmp igx-cmp-${x.compare.dir}`} title={x.compare.basis}><span aria-hidden="true">{x.compare.text}</span><span className="sr-only">{x.compare.sr}</span></small>}
                  </div>
                </li>
              ))}
            </ul>
            {view.tiles.some((x) => x.compare) && (
              <p className="igx-hint">{t("Vergleich aus den Tageswerten von Instagram, nur wo für jeden Tag beider Zeiträume ein Wert vorliegt und ihre Summe zur Zahl auf der Kachel passt. Erreichte Konten zählen Menschen und lassen sich nicht aus Tagen zusammenzählen; dafür gibt es keinen Vergleich.",
                "Comparison from Instagram's daily values, only where every day of both periods has a value and their sum matches the figure on the tile. Accounts reached count people and cannot be added up from days, so there is no comparison for them.")}</p>
            )}
          </section>
        )}

        {view.trend && <Verlauf trend={view.trend} lang={lang} h={h} />}

        {(split || formats.length > 0) && (
          <div className="igx-cols">
            {split && <Ring b={split} lang={lang} h={h} />}
            {formats.map((b) => <Formate key={b.key} b={b} lang={lang} h={h} />)}
          </div>
        )}

        {view.stories && <Storys s={view.stories} lang={lang} h={h} />}

        {view.posts && <TopInhalte p={view.posts} lang={lang} h={h} />}

        {view.monthly && <Monatswerte m={view.monthly} lang={lang} h={h} />}
      </div>

      {hasAudience && (
        <div role={tabs ? "tabpanel" : undefined} id={`${tabId}-audience-panel`} aria-labelledby={tabs ? `${tabId}-audience` : undefined} hidden={!showAudience} className="igx-panel">
          <Zielgruppe blocks={view.audience} lang={lang} h={h} note={view.audienceNote} />
        </div>
      )}

      {view.empty && (
        <section className="igx-card">
          <Ue as={h}>{t("Noch keine Kennzahlen", "No metrics yet")}</Ue>
          <p>{own
            ? t("Für diesen Zeitraum liegen noch keine Kennzahlen von Instagram vor. Nach dem ersten Abgleich erscheinen sie hier.",
              "There are no Instagram metrics for this period yet. They appear here after the first sync.")
            : t("Für diese Freigabe liegen noch keine Kennzahlen von Instagram vor. Bitte wenden Sie sich an Ihre Kontaktperson.",
              "There are no Instagram metrics for this share yet. Please contact your contact person.")}</p>
        </section>
      )}

      {own ? (
        <section className="igx-card igx-note">
          <Ue as={h}>{t("Woher die Zahlen kommen", "Where the numbers come from")}</Ue>
          {view.missingLabels.length > 0 && (
            <p>{t("Ohne Wert von Instagram:", "No value from Instagram:")} {view.missingLabels.join(", ")}. {t("Fehlende Werte bleiben leer und werden nie als null gezählt.", "Missing values stay empty and are never counted as zero.")}</p>
          )}
          <p>
            {t("Die Zeitraumwerte, die Zielgruppe und die Werte je Beitrag stammen unverändert aus der Insights-Schnittstelle von Instagram und tragen deren Bezeichnungen: Erreichte Konten zählen Menschen, Aufrufe zählen Ansichten, Interaktionen zählen Aktionen.",
              "The period values, the audience and the values per post come unchanged from the Instagram Insights interface and carry its names: accounts reached count people, views count impressions, interactions count actions.")}
          </p>
          <p>{t("Deine Agentur sieht dieselben Zahlen. An Marken gehen nur die Bereiche, die du unten freigibst.", "Your agency sees the same numbers. Brands only receive the areas you allow below.")}</p>
        </section>
      ) : (
        <section className="igx-card igx-note">
          <Ue as={h}>{t("Freigabeumfang", "Scope of this share")}</Ue>
          <p>
            {t("Diese Ansicht enthält ausschließlich freigegebene Kennzahlen. Freigegeben:", "This view contains only shared metrics. Shared:")} {view.sharedLabels.join(", ") || t("nichts", "nothing")}. {t("Zeitraum:", "Period:")} {view.periodLabel}.
          </p>
          {view.missingLabels.length > 0 && (
            <p>{t("Ohne Wert von Instagram:", "No value from Instagram:")} {view.missingLabels.join(", ")}. {t("Fehlende Werte bleiben leer und werden nie als null gezählt.", "Missing values stay empty and are never counted as zero.")}</p>
          )}
          <p>
            {t("Die Zeitraumwerte, die Zielgruppe und die Werte je Beitrag stammen unverändert aus der Insights-Schnittstelle von Instagram und tragen deren Bezeichnungen: Erreichte Konten zählen Menschen, Aufrufe zählen Ansichten, Interaktionen zählen Aktionen.",
              "The period values, the audience and the values per post come unchanged from the Instagram Insights interface and carry its names: accounts reached count people, views count impressions, interactions count actions.")}
          </p>
          <p>{t("Der Zugang ist personengebunden. Aufrufe werden protokolliert.", "Access is personal. Views are logged.")}</p>
        </section>
      )}
      {api.mode === "demo" && !embedded && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p>}
    </div>
  );
}

/** Verlauf der Tageswerte als Fläche mit Verlauf (wie die Kurven im Professional Dashboard) */
function Verlauf({ trend, lang, h }: { trend: NonNullable<InsightsView["trend"]>; lang: InsightsLang; h: Stufe }) {
  const t = tt(lang);
  const pts = trend.points;
  const W = 600, H = 150, pad = 6;
  const max = Math.max(1, ...pts.map((p) => p.value));
  const x = (i: number) => pad + (i * (W - 2 * pad)) / Math.max(1, pts.length - 1);
  const y = (v: number) => H - pad - (v / max) * (H - 2 * pad - 10);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const sum = pts.reduce((a, p) => a + p.value, 0);
  const best = pts.reduce((a, p) => (p.value > a.value ? p : a), pts[0]!);
  const day = (d: string) => new Date(`${d}T00:00:00Z`).toLocaleDateString(lang === "en" ? "en-GB" : "de-DE", { day: "numeric", month: "short", timeZone: "UTC" });
  return (
    <section className="igx-card">
      <Ue as={h}>{t(`${trend.label} je Tag`, `${trend.label} per day`)}</Ue>
      <div className="igx-trend-head"><b className="num">{countText(sum, lang)}</b><span>{t(`in ${pts.length} Tagen · stärkster Tag ${day(best.day)}`, `in ${pts.length} days · best day ${day(best.day)}`)}</span></div>
      <svg className="igx-trend" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img" aria-label={t(`${trend.label} je Tag, ${pts.length} Tage, Höchstwert ${countText(best.value, lang)}`, `${trend.label} per day, ${pts.length} days, peak ${countText(best.value, lang)}`)}>
        <defs>
          <linearGradient id="igx-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--igx-b)" stopOpacity=".32" /><stop offset="1" stopColor="var(--igx-b)" stopOpacity="0" /></linearGradient>
          <linearGradient id="igx-stroke" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="var(--igx-a)" /><stop offset=".5" stopColor="var(--igx-b)" /><stop offset="1" stopColor="var(--igx-c)" /></linearGradient>
        </defs>
        <path d={`${line} L${x(pts.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`} fill="url(#igx-fill)" />
        <path d={line} fill="none" stroke="url(#igx-stroke)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="igx-trend-axis" aria-hidden="true"><span>{day(pts[0]!.day)}</span><span>{day(pts[pts.length - 1]!.day)}</span></div>
    </section>
  );
}

/** Follower und Nicht-Follower als Ring (Instagram: Lila und Pink), die Zahlen daneben als Legende */
function Ring({ b, lang, h }: { b: Breakdown; lang: InsightsLang; h: Stufe }) {
  const list = b.list!;
  const total = list.reduce((a, e) => a + Math.max(0, e.value), 0) || 1;
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  const colors = ["var(--igx-a)", "var(--igx-b)", "var(--igx-c)", "var(--igx-d)"];
  // Follower zuerst (Lila), dann Nicht-Follower (Pink), wie in der App
  const sorted = [...list].sort((x, y) => (x.key.startsWith("non") ? 1 : 0) - (y.key.startsWith("non") ? 1 : 0));
  return (
    <section className="igx-card">
      <Ue as={h}>{b.title}</Ue>
      <div className="igx-ring">
        <svg viewBox="0 0 128 128" aria-hidden="true">
          <circle cx="64" cy="64" r={R} fill="none" stroke="var(--igx-track)" strokeWidth="12" />
          {sorted.map((e, i) => {
            const len = (Math.max(0, e.value) / total) * C;
            const el = <circle key={e.key} cx="64" cy="64" r={R} fill="none" stroke={colors[i % colors.length]} strokeWidth="12" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} transform="rotate(-90 64 64)" />;
            offset += len;
            return el;
          })}
        </svg>
        <ul className="igx-legend">
          {sorted.map((e, i) => (
            <li key={e.key}><i style={{ background: colors[i % colors.length] }} /><span>{e.label}</span><b className="num">{b.kind === "share" ? shareText(e.value, lang) : `${shareText(e.value / total, lang)}`}</b><small className="num">{b.kind === "share" ? "" : countText(e.value, lang)}</small></li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Content-Arten (Reels, Beiträge, Storys, Karussells) als Balken mit Umschalter der Kennzahl; die Tabelle bleibt zum Aufklappen */
function Formate({ b, lang, h }: { b: Breakdown; lang: InsightsLang; h: Stufe }) {
  const t = tt(lang);
  // Gewählte Kennzahl nach Schlüssel merken: andere Zeiträume liefern andere Spalten (90 Tage ohne Reichweite), sonst bliebe der Block leer
  const [colKey, setColKey] = useState<string | null>(null);
  if (b.list) {
    const max = Math.max(1, ...b.list.map((e) => e.value));
    return (
      <section className="igx-card">
        <Ue as={h}>{b.title}</Ue>
        <Balken rows={b.list.map((e) => ({ key: e.key, label: e.label, value: e.value, display: b.kind === "share" ? shareText(e.value, lang) : countText(e.value, lang) }))} max={max} />
      </section>
    );
  }
  const table = b.table!;
  const col = Math.max(0, table.columns.findIndex((c) => c.key === colKey));
  const rows = table.rows.map((r) => ({ key: r.key, label: r.label, value: r.values[col] ?? null })).filter((r): r is { key: string; label: string; value: number } => r.value !== null);
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <section className="igx-card">
      <Ue as={h}>{b.title}</Ue>
      {table.columns.length > 1 && (
        <div className="igx-seg" role="group" aria-label={t("Kennzahl wählen", "Choose metric")}>
          {table.columns.map((c, i) => <button key={c.key} type="button" aria-pressed={i === col} onClick={() => setColKey(c.key)}>{c.label}</button>)}
        </div>
      )}
      <Balken rows={rows.sort((x, y) => y.value - x.value).map((r) => ({ ...r, display: countText(r.value, lang) }))} max={max} />
      <details className="igx-more">
        <summary>{t("Alle Werte als Tabelle", "All values as a table")}</summary>
        <div className="tablewrap tk-cardtable tk-ins-table">
          <table>
            <caption className="sr-only">{b.title}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Format", "Format")}</th>
                {table.columns.map((c) => <th key={c.key} scope="col" className="money">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r) => (
                <tr key={r.key}>
                  <td data-label={t("Format", "Format")}>{r.label}</td>
                  {r.values.map((v, i) => <td key={i} data-label={table.columns[i]?.label}><span className="num">{v === null ? "–" : countText(v, lang)}</span></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function Balken({ rows, max }: { rows: { key: string; label: string; value: number; display: string }[]; max: number }) {
  return (
    <ul className="igx-bars">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="igx-bar-top"><span>{r.label}</span><b className="num">{r.display}</b></div>
          <div className="igx-bar" aria-hidden="true"><i style={{ width: `${Math.max(2, (r.value / max) * 100)}%` }} /></div>
        </li>
      ))}
    </ul>
  );
}

const KIND_ICON: Partial<Record<PostKind, "reel" | "carousel">> = { reel: "reel", carousel: "carousel" };

/** Top-Inhalte als Raster mit Vorschaubildern wie im Profil; Werte je Beitrag beim Überfahren, die Tabelle zum Aufklappen */
function TopInhalte({ p, lang, h }: { p: PostsView; lang: InsightsLang; h: Stufe }) {
  const t = tt(lang);
  const icon: Record<string, "heart" | "comment" | "save" | "share" | null> = { likes: "heart", comments: "comment", saved: "save", shares: "share" };
  return (
    <section className="igx-card">
      <Ue as={h}>{t("Top-Beiträge", "Top posts")}</Ue>
      <p className="igx-hint">{t("Nach Aufrufen · Die Werte zählen seit Veröffentlichung und hängen nicht am gewählten Zeitraum.", "By views · The values count since publication and do not depend on the selected period.")}</p>
      <ul className="igx-grid">
        {p.rows.map((r) => {
          const inner = (
            <>
              {r.thumb ? <img src={r.thumb} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <span className={`igx-ph igx-ph-${r.kind}`} aria-hidden="true"><Icon name={KIND_ICON[r.kind] ?? "grid"} /></span>}
              {KIND_ICON[r.kind] && <span className="igx-kind" aria-hidden="true"><Icon name={KIND_ICON[r.kind]!} /></span>}
              {r.views !== null && <span className="igx-views" aria-hidden="true"><Icon name="play" /><b className="num">{countText(r.views, lang)}</b></span>}
              <span className="igx-over" aria-hidden="true">
                {p.columns.map((c, i) => (icon[c.key] && r.values[i] !== null ? <span key={c.key}><Icon name={icon[c.key]!} /><b className="num">{countText(r.values[i]!, lang)}</b></span> : null))}
              </span>
              <span className="sr-only">{r.type}{r.date ? `, ${r.date}` : ""}{r.views !== null ? `, ${countText(r.views, lang)} ${t("Aufrufe", "views")}` : ""}</span>
            </>
          );
          return (
            <li key={r.key}>
              {r.url ? (
                <a className="igx-tile" href={r.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                  {inner}<span className="sr-only">{t(" auf Instagram öffnen (neues Fenster)", " open on Instagram (new window)")}</span>
                </a>
              ) : <div className="igx-tile">{inner}</div>}
            </li>
          );
        })}
      </ul>
      <details className="igx-more">
        <summary>{t("Alle Werte als Tabelle", "All values as a table")}</summary>
        <div className="tablewrap tk-cardtable tk-ins-table">
          <table>
            <caption className="sr-only">{t("Top-Beiträge", "Top posts")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Beitrag", "Post")}</th>
                {p.columns.map((c) => <th key={c.key} scope="col" className="money">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {p.rows.map((r) => (
                <tr key={r.key}>
                  <td data-label={t("Beitrag", "Post")}>
                    {r.url ? (
                      <a href={r.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
                        {r.type}<span className="sr-only">{t(" auf Instagram öffnen (neues Fenster)", " open on Instagram (new window)")}</span>
                      </a>
                    ) : r.type}
                    <small>{r.date ?? t("ohne Datum", "no date")}</small>
                  </td>
                  {r.values.map((v, i) => <td key={i} data-label={p.columns[i]?.label}><span className="num">{v === null ? "–" : countText(v, lang)}</span></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

/**
 * Storys einzeln (0340): Werte je Story, gemessen bei jedem Abgleich, solange Instagram sie herausgibt (24 Stunden). Ehrlich
 * beschriftet: letzte Messung, laufende Storys, zu wenige Betrachter; Klicks auf Link-Sticker gibt die Schnittstelle nicht her.
 */
function Storys({ s, lang, h }: { s: StoriesView; lang: InsightsLang; h: Stufe }) {
  const t = tt(lang);
  return (
    <section className="igx-card">
      <Ue as={h}>{t("Storys einzeln", "Individual stories")}</Ue>
      {s.avgReach && (
        <div className="igx-trend-head"><b className="num">{s.avgReach.display}</b><span>{t(`erreichte Konten je Story im Schnitt · ${s.avgReach.count} gemessene Storys`, `accounts reached per story on average · ${s.avgReach.count} measured stories`)}</span></div>
      )}
      <div className="tablewrap tk-cardtable tk-ins-table">
        <table>
          <caption className="sr-only">{t("Storys einzeln", "Individual stories")}</caption>
          <thead>
            <tr>
              <th scope="col">{t("Story", "Story")}</th>
              {s.columns.map((c) => <th key={c.key} scope="col" className="money">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {s.rows.map((r) => (
              <tr key={r.key}>
                <td className="igx-story-kopf">
                  <span className="igx-story-wann">{r.type} · {r.when}</span>
                  {r.live && <span className="igx-live">{t("läuft noch", "still live")}</span>}
                  <small>{r.few ? t("Weniger als fünf Betrachter: Instagram nennt keine Werte.", "Fewer than five viewers: Instagram provides no values.") : r.measured ?? t("noch nicht gemessen", "not measured yet")}</small>
                </td>
                {r.values.map((v, i) => <td key={i} data-label={s.columns[i]?.label}><span className="num">{v === null ? "–" : countText(v, lang)}</span></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="igx-hint">
        {t("Instagram gibt Werte je Story nur 24 Stunden heraus. CreatorOffice misst jede Story bei jedem Abgleich (viermal am Tag), solange sie läuft; was danach noch dazukam, fehlt. Klicks auf Link-Sticker liefert Instagram über diese Schnittstelle nicht, sie lassen sich nur per Bildschirmfoto aus der App belegen.",
          "Instagram releases values per story for 24 hours only. CreatorOffice measures each story at every sync (four times a day) while it is live; anything added afterwards is missing. Instagram does not provide link sticker taps through this interface; they can only be shown with a screenshot from the app.")}
      </p>
    </section>
  );
}

/** Zielgruppe wie im Reiter „Follower“ der App: Umschalter zwischen Städten, Ländern, Alter und Geschlecht */
function Zielgruppe({ blocks, lang, h, note }: { blocks: AudienceBlock[]; lang: InsightsLang; h: Stufe; note?: string }) {
  const t = tt(lang);
  const order = ["cities", "countries", "age", "gender"];
  const sorted = [...blocks].sort((x, y) => order.indexOf(x.block) - order.indexOf(y.block));
  const [active, setActive] = useState(sorted[0]!.block);
  const a = sorted.find((x) => x.block === active) ?? sorted[0]!;
  return (
    <section className="igx-card">
      <Ue as={h}>{t("Zielgruppe", "Audience")}</Ue>
      {note && <p className="igx-hint igx-zeitfenster">{note}</p>}
      <div className="igx-seg" role="tablist" aria-label={t("Zielgruppe nach", "Audience by")}>
        {sorted.map((x) => <button key={x.block} type="button" role="tab" aria-selected={x.block === a.block} onClick={() => setActive(x.block)}>{x.title}</button>)}
      </div>
      {/* Alle Reiter stehen im Dokument (Vorlesen, Suche), sichtbar ist nur der gewählte */}
      {sorted.map((x) => (
        <div key={x.block} role="tabpanel" aria-label={x.title} hidden={x.block !== a.block}>
          {x.entries.length
            ? <Balken rows={x.entries.map((e) => ({ key: e.key, label: e.label, value: e.value, display: e.display }))} max={Math.max(1e-9, ...x.entries.map((e) => e.value))} />
            : <p>{t("Kein Wert von Instagram.", "No value from Instagram.")}</p>}
        </div>
      ))}
      <p className="source-note">
        {a.kind === "share"
          ? t("Anteile laut Instagram, nur zusammengefasst herausgegeben.", "Shares according to Instagram, released only in aggregate.")
          : t("Anzahl der Konten laut Instagram, nur zusammengefasst herausgegeben.", "Number of accounts according to Instagram, released only in aggregate.")}
      </p>
    </section>
  );
}

function Monatswerte({ m, lang, h }: { m: MonthlyView; lang: InsightsLang; h: Stufe }) {
  const t = tt(lang);
  const first = m.columns[0]!;
  const bars = m.rows.filter((r) => r.values[0] !== null).map((r) => ({ label: `${lang === "en" ? new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" }).format(new Date(`${r.month}-01T00:00:00Z`)) : monthShort(r.month)} ${r.month.slice(2, 4)}`, value: r.values[0]! }));
  return (
    <section className="igx-card">
      <Ue as={h}>{t("Monatswerte", "Monthly values")}</Ue>
      <p className="igx-hint">
        {t("Instagram gibt Kontokennzahlen nur 90 Tage rückwirkend heraus. Die Monatswerte sind deshalb die Summe der Tageswerte je Monat; erreichte Konten werden dafür nie aus Tageswerten summiert und fehlen, solange Instagram keinen Monatswert liefert.",
          "Instagram releases account metrics only 90 days back. The monthly values are therefore the sum of the daily values per month; accounts reached are never summed from daily values and stay empty until Instagram provides a monthly value.")}
      </p>
      {bars.length > 1 && <div className="igx-monthbars"><BarChart items={bars} format={(v) => countText(v, lang)} caption={t(`${first.label} je Monat`, `${first.label} per month`)} /></div>}
      <details className="igx-more" open={bars.length <= 1}>
        <summary>{t("Alle Werte als Tabelle", "All values as a table")}</summary>
        <div className="tablewrap tk-cardtable tk-ins-table">
          <table>
            <caption className="sr-only">{t("Monatswerte", "Monthly values")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("Monat", "Month")}</th>
                {m.columns.map((c) => <th key={c.key} scope="col" className="money">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {m.rows.map((r) => (
                <tr key={r.month}>
                  <td data-label={t("Monat", "Month")}>{r.label}</td>
                  {r.values.map((v, i) => <td key={i} data-label={m.columns[i]?.label}><span className="num">{v === null ? "–" : countText(v, lang)}</span></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}
